import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";
import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { LegacyFirestoreUsersSearchForm } from "@/pages/legacy-firestore-users/root/legacy-firestore-users-search-form";
import { LegacyFirestoreUsersTable } from "@/pages/legacy-firestore-users/root/legacy-firestore-users-table";

export const LegacyFirestoreUsersRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query parameters
  const pageSize = 50;
  const initialQuery = searchParams.get("query") ?? "";
  const [searchTerm, setSearchTerm] = useState<string>(initialQuery);

  // Update searchTerm when URL changes
  useEffect(() => {
    const query = searchParams.get("query") ?? "";
    setSearchTerm(query);
  }, [searchParams]);

  // React Query to perform the search based on searchTerm
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["legacy-firestore-users-search", searchTerm],
    queryFn: () =>
      api.post("/internal/v1/legacy-firestore-users/search", {
        body: {
          query_string: searchTerm,
          limit: pageSize,
        },
      }),
    enabled: searchTerm.length > 0,
    // 5 minutes
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleViewLegacyFirestoreUser = (id: string) => {
    navigate(atlasRoutes.legacyFirestoreUsersView({ params: { legacyFirestoreUserId: id } }).path);
  };

  const handleSearch = (query: string) => {
    if (query) {
      searchParams.set("query", query);
    } else {
      searchParams.delete("query");
    }
    setSearchParams(searchParams);
  };

  // Prepare the data for the table
  const tableData = useMemo(() => data?.data?.results ?? [], [data]);

  return (
    <Container>
      <h1>Legacy Firestore Users</h1>
      <DescriptionText>Search by name or email address.</DescriptionText>
      <br />
      {/* Search Form */}
      <LegacyFirestoreUsersSearchForm initialValue={searchTerm} onSearch={handleSearch} />
      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching accounts: {error.message}</div>}
      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <LegacyFirestoreUsersTable
          data={tableData}
          onViewLegacyFirestoreUser={handleViewLegacyFirestoreUser}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && searchTerm && <div>No legacy firestore users found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
`;
